import { useTranslation } from 'react-i18next';
import type { StepId } from '../App.js';

/**
 * Progress through the flow.
 *
 * A guest may tap back to any step they have already completed, but not forward
 * past one they have not — jumping to "your details" before a time is chosen
 * produces a form that cannot be submitted, which reads as broken.
 *
 * Numbers are the step index, not decoration: the flow genuinely is a sequence
 * where each step narrows the next.
 */
const LABELS: Record<StepId, string> = {
  party: 'common.partySize',
  date: 'common.date',
  slot: 'common.time',
  details: 'common.guest',
  done: 'common.confirm',
};

export function StepRail({
  steps,
  current,
  onGoTo,
}: {
  steps: StepId[];
  current: StepId;
  onGoTo: (step: StepId) => void;
}) {
  const { t } = useTranslation();
  const currentIndex = steps.indexOf(current);

  return (
    <ol className="flex items-center gap-1" aria-label={t('common.confirm')}>
      {steps.map((step, index) => {
        const done = index < currentIndex;
        const active = index === currentIndex;

        return (
          <li key={step} className="flex flex-1 items-center gap-1">
            <button
              type="button"
              disabled={!done && !active}
              onClick={() => onGoTo(step)}
              aria-current={active ? 'step' : undefined}
              className={[
                'flex w-full flex-col items-center gap-1 rounded-md px-1 py-1.5 text-[11px] transition-colors',
                active ? 'text-brand-teal-dark font-semibold' : '',
                done ? 'text-slate-600 hover:bg-brand-teal-light' : '',
                !done && !active ? 'text-slate-300' : '',
              ].join(' ')}
            >
              <span
                className={[
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                  active ? 'bg-brand-teal text-white' : '',
                  done ? 'bg-brand-teal-light text-brand-teal-dark' : '',
                  !done && !active ? 'bg-slate-100 text-slate-400' : '',
                ].join(' ')}
                aria-hidden
              >
                {index + 1 /* i18n-ok: step number */}
              </span>
              <span className="truncate">{t(LABELS[step])}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
